import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './ParentSelector.css';

/**
 * Parent task selector component for hierarchy
 */
const ParentSelector = ({
  projectId,
  taskId,
  taskType,
  currentParentId,
  onChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [potentialParents, setPotentialParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentParent, setCurrentParent] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Calculate dropdown position
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 350; // max dropdown height
    const dropdownWidth = Math.max(280, rect.width); // min-width is 280px

    // Prefer opening below - only open above if there's insufficient space below
    // AND significantly more space above (at least 100px more)
    const openAbove = spaceBelow < 200 && spaceAbove > (spaceBelow + 100);

    // Calculate left position and adjust if dropdown would go off-screen
    // Use very large safety margin (50px) for side panels where space is constrained
    const safetyMargin = 50;
    let leftPosition = rect.left;
    const dropdownRight = leftPosition + dropdownWidth;

    // If dropdown extends beyond right edge of viewport, shift it left
    if (dropdownRight > viewportWidth - safetyMargin) {
      leftPosition = Math.max(safetyMargin, viewportWidth - dropdownWidth - safetyMargin);
    }

    // Ensure dropdown doesn't go off left edge
    if (leftPosition < safetyMargin) {
      leftPosition = safetyMargin;
    }

    setDropdownPosition({
      top: openAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: leftPosition,
      width: rect.width,
      openAbove
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isInsideTrigger = triggerRef.current?.contains(e.target);
      const isInsideDropdown = dropdownRef.current?.contains(e.target);

      if (!isInsideTrigger && !isInsideDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    calculatePosition();

    const handlePositionUpdate = () => {
      calculatePosition();
    };

    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isOpen, calculatePosition]);

  // Fetch potential parents
  const fetchPotentialParents = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      let url;
      if (taskId) {
        url = `/hierarchy/tasks/${taskId}/potential-parents`;
      } else {
        url = `/hierarchy/projects/${projectId}/potential-parents?task_type=${taskType}`;
      }
      if (searchTerm) {
        url += `${url.includes('?') ? '&' : '?'}search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await api.get(url);
      setPotentialParents(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch potential parents:', error);
      setPotentialParents([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, taskId, taskType]);

  // Fetch current parent info
  useEffect(() => {
    if (currentParentId) {
      api.get(`/tasks/${currentParentId}`)
        .then(res => setCurrentParent(res.data))
        .catch(() => setCurrentParent(null));
    } else {
      setCurrentParent(null);
    }
  }, [currentParentId]);

  // Fetch parents when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchPotentialParents(search);
    }
  }, [isOpen, fetchPotentialParents, search]);

  const handleSelect = (parent) => {
    onChange(parent ? parent.id : null);
    setIsOpen(false);
    setSearch('');
  };

  const handleRemoveParent = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const getTypeIcon = (type) => {
    const icons = {
      epic: '⚡',
      task: '✓',
      story: '★',
      bug: '⚠',
      subtask: '•',
    };
    return icons[type] || '✓';
  };

  const getTypeColor = (type) => {
    const colors = {
      epic: '#6554c0',     // Purple
      story: '#36b37e',    // Green
      task: '#4c9aff',     // Blue
      subtask: '#4c9aff',  // Blue
      bug: '#ff5630',      // Red
    };
    return colors[type] || '#4c9aff';
  };

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="parent-selector">
      <div
        ref={triggerRef}
        className={`parent-selector-trigger ${disabled ? 'disabled' : ''}`}
        onClick={handleToggle}
      >
        {currentParent ? (
          <div className="selected-parent">
            <span
              className="parent-type-icon"
              style={{ backgroundColor: getTypeColor(currentParent.task_type) }}
            >
              {getTypeIcon(currentParent.task_type)}
            </span>
            <span className="parent-key">{currentParent.task_key}</span>
            <span className="parent-title">{currentParent.title}</span>
            {!disabled && (
              <button
                className="remove-parent-btn"
                onClick={handleRemoveParent}
                title="Remove parent"
              >
                ×
              </button>
            )}
          </div>
        ) : (
          <span className="placeholder">Select parent...</span>
        )}
        <span className="dropdown-arrow">▾</span>
      </div>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={`parent-selector-dropdown ${dropdownPosition.openAbove ? 'open-above' : ''}`}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 10001 // Above modal overlays
          }}
        >
          <div className="parent-search">
            <input
              type="text"
              placeholder="Search by key or title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="parent-options">
            {/* Option to remove parent */}
            {currentParentId && (
              <div
                className="parent-option remove-option"
                onClick={() => handleSelect(null)}
              >
                <span className="no-parent-icon">∅</span>
                <span>No parent (top level)</span>
              </div>
            )}

            {loading ? (
              <div className="parent-loading">Loading...</div>
            ) : potentialParents.length > 0 ? (
              potentialParents.map((parent) => (
                <div
                  key={parent.id}
                  className={`parent-option ${parent.id === currentParentId ? 'selected' : ''}`}
                  onClick={() => handleSelect(parent)}
                >
                  <span
                    className="parent-type-icon"
                    style={{ backgroundColor: getTypeColor(parent.task_type) }}
                  >
                    {getTypeIcon(parent.task_type)}
                  </span>
                  <div className="parent-option-info">
                    <span className="parent-key">{parent.task_key}</span>
                    <span className="parent-title">{parent.title}</span>
                  </div>
                  {parent.child_count > 0 && (
                    <span className="child-count">{parent.child_count} children</span>
                  )}
                </div>
              ))
            ) : (
              <div className="no-parents">No eligible parents found</div>
            )}
          </div>
        </div>,
        document.body // Render dropdown to body to prevent clipping
      )}
    </div>
  );
};

/**
 * Compact parent display for task cards/lists
 */
export const ParentBadge = ({ parent, onClick }) => {
  if (!parent) return null;

  const getTypeIcon = (type) => {
    const icons = { epic: '⚡', task: '✓', story: '★', bug: '⚠', subtask: '•' };
    return icons[type] || '✓';
  };

  const getTypeColor = (type) => {
    const colors = {
      epic: '#6554c0',     // Purple
      story: '#36b37e',    // Green
      task: '#4c9aff',     // Blue
      subtask: '#4c9aff',  // Blue
      bug: '#ff5630',      // Red
    };
    return colors[type] || '#4c9aff';
  };

  return (
    <span
      className="parent-badge"
      onClick={onClick}
      title={`Parent: ${parent.task_key} - ${parent.title}`}
    >
      <span
        className="parent-type-icon-sm"
        style={{ backgroundColor: getTypeColor(parent.task_type) }}
      >
        {getTypeIcon(parent.task_type)}
      </span>
      <span className="parent-key-sm">{parent.task_key}</span>
    </span>
  );
};

/**
 * Children count badge
 */
export const ChildrenBadge = ({ count, openCount, onClick }) => {
  if (!count) return null;

  return (
    <span
      className={`children-badge ${openCount > 0 ? 'has-open' : 'all-done'}`}
      onClick={onClick}
      title={`${count} children (${openCount} open)`}
    >
      📦 {count}
    </span>
  );
};

/**
 * Task type badge with icon
 */
export const TaskTypeBadge = ({ type }) => {
  const getTypeIcon = (type) => {
    const icons = { epic: '⚡', task: '✓', story: '★', bug: '⚠', subtask: '•' };
    return icons[type] || '✓';
  };

  const getTypeColor = (type) => {
    const colors = {
      epic: '#6554c0',     // Purple
      story: '#36b37e',    // Green
      task: '#4c9aff',     // Blue
      subtask: '#4c9aff',  // Blue
      bug: '#ff5630',      // Red
    };
    return colors[type] || '#4c9aff';
  };

  return (
    <span
      className="task-type-badge-icon"
      style={{ backgroundColor: getTypeColor(type) }}
      title={type}
    >
      {getTypeIcon(type)}
    </span>
  );
};

export default ParentSelector;
